import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, Wine, UserCheck } from "lucide-react";
import { api, formatError, fmtDateTime } from "../../lib/api";
import { AdminHeader } from "../../components/AdminLayout";

const GROUP_LABEL = { classes: "Classes", socials: "Socials", events: "Events", weekender: "Weekender" };
const sgtDay = (iso) => {
  if (!iso) return "";
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Singapore", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(iso));
};
const todaySGT = () => sgtDay(new Date().toISOString());
const addDaysSGT = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return sgtDay(d.toISOString()); };

export default function AdminRedemptions() {
  const [items, setItems] = useState([]);
  const [kindFilter, setKindFilter] = useState("all");   // all | checkin | drink
  const [catFilter, setCatFilter] = useState("all");     // all | <group>
  const [dateMode, setDateMode] = useState("all");       // all | today | week | custom
  const [customDate, setCustomDate] = useState("");

  const load = useCallback(() => api.get("/admin/redemptions").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const r = await api.get("/admin/redemptions/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a"); a.href = url; a.download = "redemptions.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const groups = useMemo(() => [...new Set(items.map((i) => i.group).filter(Boolean))], [items]);

  const filtered = useMemo(() => {
    const t = todaySGT(), wk = addDaysSGT(-6);
    return items.filter((i) => {
      if (kindFilter !== "all" && i.kind !== kindFilter) return false;
      if (catFilter !== "all" && i.group !== catFilter) return false;
      if (dateMode !== "all") {
        const d = sgtDay(i.created_at);
        if (dateMode === "today" && d !== t) return false;
        if (dateMode === "week" && !(d >= wk && d <= t)) return false;
        if (dateMode === "custom" && customDate && d !== customDate) return false;
      }
      return true;
    });
  }, [items, kindFilter, catFilter, dateMode, customDate]);

  const drinks = filtered.filter((i) => i.kind === "drink").length;
  const checkins = filtered.filter((i) => i.kind === "checkin").length;

  const chip = (active) => `rounded-full px-4 py-1.5 text-sm border transition-colors ${active ? "bg-[#9D4EDD]/20 border-[#9D4EDD]/40 text-white" : "bg-white/5 border-white/10 text-[#A19DB0]"}`;
  const dchip = (active) => `rounded-full px-4 py-1.5 text-sm border transition-colors ${active ? "bg-[#D4AF37]/15 border-[#D4AF37]/40 text-[#D4AF37]" : "bg-white/5 border-white/10 text-[#A19DB0]"}`;

  return (
    <div>
      <AdminHeader title="Redemption log" subtitle={`${filtered.length} entr${filtered.length !== 1 ? "ies" : "y"} · ${drinks} drink${drinks !== 1 ? "s" : ""} · ${checkins} check-in${checkins !== 1 ? "s" : ""}`}
        action={<button onClick={exportCsv} data-testid="export-redemptions" className="flex items-center gap-2 bg-[#D4AF37] text-black font-semibold rounded-full px-4 py-2.5 text-sm active:scale-95 transition-transform"><Download size={16} /> Export CSV</button>} />

      {/* Type filter */}
      <div className="flex flex-wrap gap-2 mb-3">
        {[["all", "All types"], ["checkin", "Check-ins"], ["drink", "Drinks"]].map(([v, l]) => (
          <button key={v} onClick={() => setKindFilter(v)} data-testid={`redemption-kind-${v}`} className={chip(kindFilter === v)}>{l}</button>
        ))}
      </div>

      {/* Category filter */}
      <div className="flex flex-wrap gap-2 mb-3">
        <button onClick={() => setCatFilter("all")} data-testid="redemption-cat-all" className={chip(catFilter === "all")}>All categories</button>
        {groups.map((g) => (
          <button key={g} onClick={() => setCatFilter(g)} data-testid={`redemption-cat-${g}`} className={chip(catFilter === g)}>{GROUP_LABEL[g] || g}</button>
        ))}
      </div>

      {/* Date filter */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {[["all", "All dates"], ["today", "Today"], ["week", "Last 7 days"]].map(([v, l]) => (
          <button key={v} onClick={() => setDateMode(v)} data-testid={`redemption-date-${v}`} className={dchip(dateMode === v)}>{l}</button>
        ))}
        <input type="date" value={customDate} onChange={(e) => { setCustomDate(e.target.value); setDateMode(e.target.value ? "custom" : "all"); }} data-testid="redemption-date-custom"
          className={`rounded-full px-4 py-1.5 text-sm border outline-none ${dateMode === "custom" ? "bg-[#D4AF37]/15 border-[#D4AF37]/40 text-[#D4AF37]" : "bg-white/5 border-white/10 text-[#A19DB0]"}`} />
      </div>

      {filtered.length === 0 ? (
        <p className="text-[#A19DB0] text-sm" data-testid="redemptions-empty">No transactions match these filters.</p>
      ) : (
        <div className="space-y-2" data-testid="redemptions-list">
          {filtered.map((r) => (
            <div key={r.id} data-testid={`redemption-${r.id}`} className={`flex items-center gap-3 rounded-2xl border p-4 ${r.undone ? "bg-white/[0.02] border-white/5 opacity-60" : "bg-white/5 border-white/10"}`}>
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${r.kind === "drink" ? "bg-[#D4AF37]/20" : "bg-[#9D4EDD]/20"}`}>
                {r.kind === "drink" ? <Wine size={16} className="text-[#D4AF37]" /> : <UserCheck size={16} className="text-[#c79bff]" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`font-medium truncate ${r.undone ? "line-through" : ""}`}>{r.member_name || r.member_id} · <span className="text-[#A19DB0]">{r.package_name}{r.option ? ` (${r.option})` : ""}</span></p>
                <p className="text-[#A19DB0] text-xs">
                  {r.kind === "drink" ? "Drink redeemed" : "Checked in"}
                  {r.group ? ` · ${GROUP_LABEL[r.group] || r.group}` : ""}
                  {r.social_title ? ` · ${r.social_title}` : ""}
                  {" · by "}{r.staff_name} · {fmtDateTime(r.created_at)}
                  {r.undone ? " · UNDONE" : ""}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
